import React, { useState, useRef, useCallback } from 'react';
// Step 1: Install react-webcam: npm install react-webcam
import Webcam from "react-webcam";
// Import the icons you need from lucide-react
import { Shield, Camera, Upload, Send, Map, ArrowLeft, MessageSquare, CircleDot } from 'lucide-react';

// --- API Configuration (Simulating content from 'utils/api.js') ---
import axios from 'axios';

import api from 'utils/api'; // Adjust the import path as necessary


// --- Home Page Component ---
const HomePage = ({ onNavigate }) => {
    return (
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
            <div className="text-center">
                <div className="flex justify-center items-center mb-2">
                    <Shield className="mr-2 h-6 w-6" />
                    <h1 className="text-3xl font-bold text-gray-800">SipSafe</h1>
                </div>
                <h2 className="text-xl font-semibold text-gray-600">Dashboard</h2>
                <p className="text-sm text-gray-500">Welcome! What would you like to do?</p>
            </div>

            <div className="space-y-4">
                <button
                    onClick={() => onNavigate('testPhoto')}
                    className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <Camera className="mr-2 h-5 w-5" />
                    Submit a Test
                </button>
                <button
                    onClick={() => onNavigate('map')}
                    className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                    <Map className="mr-2 h-5 w-5" />
                    View Safety Map
                </button>
            </div>
        </div>
    );
};


// --- Test Strip Photo Page Component ---
const TestStripPhotoPage = ({ onBack }) => {
    const [imageSrc, setImageSrc] = useState(null); // For previewing the image
    const [imageFile, setImageFile] = useState(null); // For sending the file to the API
    const [prompt, setPrompt] = useState(''); // State for the prompt input
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null); // 'success' or 'error'
    const [apiResponseContent, setApiResponseContent] = useState(''); // State for the API response content
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const fileInputRef = useRef(null);
    const webcamRef = useRef(null); // Ref for the webcam component

    // Video constraints to prefer the rear camera on mobile
    const videoConstraints = {
        facingMode: { exact: "environment" }
    };

    // Helper function to convert base64 data URL to a File object
    const dataURLtoFile = (dataurl, filename) => {
        let arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    // Resets the state for a new test submission
    const startNewTest = () => {
        setImageSrc(null);
        setImageFile(null);
        setPrompt('');
        setSubmissionStatus(null);
        setApiResponseContent('');
    };

    // Function to open the camera
    const handleTakePhotoClick = () => {
        startNewTest();
        setIsCameraOpen(true);
    };

    // Function to capture the photo using react-webcam
    const handleSnapPhoto = useCallback(() => {
        if (webcamRef.current) {
            const imageSrcBase64 = webcamRef.current.getScreenshot();
            setImageSrc(imageSrcBase64);
            const file = dataURLtoFile(imageSrcBase64, 'test-photo.jpg');
            setImageFile(file);
            setIsCameraOpen(false);
        }
    }, [webcamRef]);

    // Handle uploading a file from gallery
    const handleUploadClick = () => {
        startNewTest();
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImageSrc(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle submitting the test result to the AI endpoint
    const handleSubmit = async () => {
        if (!imageFile) {
            alert("Please take or upload a photo first.");
            return;
        }
        if (!prompt.trim()) {
            alert("Please enter a comment. This field is required.");
            return;
        }
        setIsSubmitting(true);
        setSubmissionStatus(null);
        setApiResponseContent('');

        const formData = new FormData();
        formData.append('photo', imageFile);
        formData.append('prompt', prompt);

        try {
            const response = await api.post('/test/ai', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            console.log("Submission successful!", response.data);
            setIsSubmitting(false);
            setSubmissionStatus('success');
            if (response.data && response.data.content) {
                setApiResponseContent(response.data.content);
            }
        } catch (error) {
            console.error("Error submitting test:", error);
            setIsSubmitting(false);
            setSubmissionStatus('error');
        }
    };

    const handleCancelCamera = () => {
        setIsCameraOpen(false);
    };

    return (
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
            <button onClick={onBack} className="flex items-center text-sm font-medium text-gray-600 hover:text-indigo-600 mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
            </button>
            <div className="text-center">
                <div className="flex justify-center items-center mb-2">
                    <Shield className="mr-2 h-6 w-6" />
                    <h1 className="text-3xl font-bold text-gray-800">SipSafe</h1>
                </div>
                <h2 className="text-xl font-semibold text-gray-600">Submit a Test</h2>
                <p className="text-sm text-gray-500">Help keep the community safe.</p>
            </div>

            <div className="space-y-4">
                {isCameraOpen ? (
                    // Camera View
                    <div className="space-y-4">
                        <Webcam
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            className="w-full h-48 bg-black rounded-lg object-cover"
                            videoConstraints={videoConstraints}
                        />
                        <button onClick={handleSnapPhoto} className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700">
                            <CircleDot className="mr-2 h-5 w-5" />
                            Snap Photo
                        </button>
                        <button onClick={handleCancelCamera} className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                            Cancel
                        </button>
                    </div>
                ) : (
                    // Standard View
                    <>
                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                            {imageSrc ? (
                                <img src={imageSrc} alt="Test Strip Preview" className="object-cover w-full h-full" />
                            ) : (
                                <Camera className="h-16 w-16 text-gray-400" />
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <button onClick={handleTakePhotoClick} className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                                <Camera className="mr-2 h-5" />
                                Take Photo
                            </button>
                            {/* <button onClick={handleUploadClick} className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                                <Upload className="mr-2 h-5 w-5" />
                                Upload
                            </button> */}
                            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                        </div>

                        {/* Prompt Input Field */}
                        <div>
                            <label htmlFor="prompt" className="sr-only">Prompt</label>
                            <div className="relative">
                                <textarea
                                    id="prompt"
                                    name="prompt"
                                    rows="3"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                                    placeholder="Add a comment (e.g., 'This happened at [venue]')"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    required
                                ></textarea>
                                <MessageSquare className="h-5 w-5 text-gray-400 absolute top-3 right-3" />
                            </div>
                        </div>

                        <div>
                            <button
                                onClick={handleSubmit}
                                disabled={!imageFile || isSubmitting || submissionStatus === 'success'}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Result'}
                                <Send className="ml-2 h-5 w-5" />
                            </button>
                        </div>

                        {/* Status and Response Display */}
                        {submissionStatus === 'success' && (
                            <div className="p-3 bg-green-100 text-green-800 rounded-md text-sm space-y-2">
                                <p className="font-semibold">Test submitted successfully!</p>
                                {apiResponseContent && (
                                    <div>
                                        <h4 className="font-bold">AI Analysis:</h4>
                                        <p>{apiResponseContent}</p>
                                    </div>
                                )}
                            </div>
                        )}
                        {submissionStatus === 'error' && (
                            <div className="p-3 bg-red-100 text-red-800 rounded-md text-sm">
                                There was an error submitting your test. Please try again.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};


// --- Main App Component ---
export default function App() {
    const [currentView, setCurrentView] = useState('home');

    const handleNavigate = (view) => {
        setCurrentView(view);
    };

    const renderView = () => {
        switch (currentView) {
            case 'home':
                return <HomePage onNavigate={handleNavigate} />;
            case 'testPhoto':
                return <TestStripPhotoPage onBack={() => handleNavigate('home')} />;
            default:
                return <HomePage onNavigate={handleNavigate} />;
        }
    };

    return (
        <main className="bg-gray-100 flex items-center justify-center min-h-screen font-sans">
            <div className="container mx-auto p-4 flex justify-center">
                {renderView()}
            </div>
        </main>
    );
}
